import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { saveOrganizationTransition, getOrganizationTransitionByOrgId } from '../../Services/OrganizationService';
import { useAuth } from '../../Context/AuthContext';
import { useParams } from 'react-router-dom';
import CustomDatePicker from '../Common/CustomDatePicker';

// Validation schema
const validationSchema = Yup.object({
  transitionedDate: Yup.string().required('Transitioned Date is required'),
  startDate: Yup.string().required('Start Date is required'),
  billFromDOS: Yup.string().required('Bill from DOS is required'),
  notes: Yup.string(),
});

export default function TransitionInfo({ onSaveSuccess, onContinue, organizationId }) {
  const { token } = useAuth();
  const { id: orgIdFromParams } = useParams();
  const [loading, setLoading] = useState(false);
  const [transitionId, setTransitionId] = useState(null);
  const [fetching, setFetching] = useState(false);

  const effectiveOrgId = organizationId || orgIdFromParams || null;

  const handleSave = async (values) => {
    setLoading(true);
    try {
      const payload = {
        id: transitionId || null,
        organization_id: values.organizationId || effectiveOrgId || null,
        transitioned_date: values.transitionedDate || null,
        start_date: values.startDate || null,
        bill_from_dos: values.billFromDOS || null,
        notes: values.notes || null,
        type: transitionId ? 'update' : 'create',
      };

      const res = await saveOrganizationTransition(payload, token);
      if (res?.is_success) {
        toast.success(res?.message || 'Transition saved');
        // update local id if returned
        const returned = res.data ?? res;
        if (returned?.id) setTransitionId(returned.id);
        onSaveSuccess?.();
        onContinue?.();
      } else if(res?.is_success === false && res?.status_code === 500){
        toast.error('Internal Server Error. Please Contact Support Team.');
        console.error('Internal Server Error. Please Contact Support Team.', res);
      } 
      else {
        toast.error(res?.message || 'Failed to save transition');
        console.error('Failed to save transition', res);
      }
    } catch (error) {
      console.error('Error saving transition info:', error);
    } finally {
      setLoading(false);
    }
  };

  const initialValues = React.useMemo(() => ({
    organizationId: effectiveOrgId,
    transitionedDate: null,
    startDate: null,
    billFromDOS: null,
    notes: '',
  }), [effectiveOrgId]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: handleSave,
  });

  // Load existing transition for organization if available
  useEffect(() => {
    const load = async () => {
      const orgId = effectiveOrgId || formik.values.organizationId || null;
      if (!orgId) return;
      setFetching(true);
      try {
        const res = await getOrganizationTransitionByOrgId(orgId, token);
        if (res?.is_success) {
          const d = res.data ?? res;
          setTransitionId(d.id ?? null);
          formik.setValues({
            organizationId: d.organization_id ?? orgId,
            transitionedDate: d.transitioned_date ?? null,
            startDate: d.start_date ?? null,
            billFromDOS: d.bill_from_dos ?? null,
            notes: d.notes ?? '',
          });
        }
      } catch (err) {
        console.error('Error loading organization transition:', err);
      } finally {
        setFetching(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveOrgId, token]);

  const getFieldError = (fieldName) => {
    return formik.errors[fieldName];
  };

  const getFieldTouched = (fieldName) => {
    return formik.touched[fieldName];
  };

  const renderFieldError = (fieldName) => {
    const error = getFieldError(fieldName);
    const touched = getFieldTouched(fieldName);
    const showError = (touched || formik.submitCount > 0) && error;
    return showError ? <small className="text-danger d-block mt-1">{error}</small> : null;
  };

  const renderAsterisk = () => <span className="text-danger">*</span>;

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <div className="py-3 px-0 px-md-3">
        {/* Transition Information Section */}
        <div className="p-4 rounded-4 border bg-light-subtle">
          <h6 className="fw-bold hc-text-primary mb-3">Transition Information</h6>

          {fetching ? (
            <div className="text-center py-5 text-muted">
              <div className="spinner position-absolute d-flex align-items-center justify-content-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Transitioned Date {renderAsterisk()}
                  </label>

                  <CustomDatePicker
                    name="transitionedDate"
                    formik={formik}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Start Date {renderAsterisk()}
                  </label>

                  <CustomDatePicker
                    name="startDate"
                    formik={formik}
                  />
                </div>
              </div>

              <div className="row g-3 mt-1">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Bill from DOS {renderAsterisk()}
                  </label>

                  <CustomDatePicker
                    name="billFromDOS"
                    formik={formik}
                  />
                </div>
              </div>

              <div className="row g-3 mt-1">
                <div className="col-12">
                  <label className="form-label fw-semibold">Notes</label>
                  <textarea
                    className={`form-control rounded-3 shadow-sm ${
                      getFieldTouched('notes') && getFieldError('notes') ? 'is-invalid' : ''
                    }`}
                    placeholder="Enter any additional notes about the transition"
                    rows="4"
                    {...formik.getFieldProps('notes')}
                  />
                  {renderFieldError('notes')}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer Actions */}
        <div className="mt-4 d-flex gap-2 justify-content-end">
          <button
            type="submit"
            disabled={loading || !formik.isValid}
            className="btn hc-bg-primary text-white px-5 py-2 rounded-3 fw-semibold shadow"
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                Saving...
              </>
            ) : (
              'Save & Continue'
            )}
          </button>
        </div>
      </div>
    </form>
  );
}
